import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  ViewEncapsulation,
  OnInit,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  SimpleChange,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BmarkHedgeOrdersTableDefinitionFactory,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  BfxBlotterAction,
  BmarkFixingData,
  ProfileSettingKeys,
  OrderPauseState,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { ReplaySubject, Subject, Subscription } from "rxjs";
import { BfxBaseBlotter, provideBlotter } from "../../../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "../../../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../../../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBlotterFilter } from "../../../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxMouseModifierKeyService } from "../../../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../../../bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "../../../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../../../golden-layout/golden-layout-service";
import { BmarkHedgeOrdersContextMenuService } from "./services/bmark-hedge-orders-context-menu.service";
import { BmarkHedgeOrdersFilterService } from "./services/bmark-hedge-orders-filter.service";
import { BmarkHedgeOrdersDataService } from "./services/bmark-hedge-orders-data.service";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { OrderTypeRenderer } from "../../../bfx-blotter-orders/renderers/OrderTypeRenderer";
import { SpotTrackingRenderer } from "../../../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { MarketMidRenderer } from "../../../bfx-blotter-orders/renderers/MarketMidRenderer";
import { PercentAwayRenderer } from "../../../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { BmarkClientOrdersBlotterMode } from "../../../../shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { RowNode } from "ag-grid-community";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { FillRenderer } from "app/bfx-blotter-orders/renderers/FillRenderer";
import { BmarkSummaryUtilService } from "../../services/bmark-summary-util.service";
import { AutoHedgeRenderer } from "app/bfx-bmark-summary/blotters/hedge-orders/renderers/AutoHedgeRenderer";
import { FillPercentRenderer } from "app/bfx-blotter-orders/renderers/FillPercentRenderer";
import {
  AlgoFilterHelper,
  AlgoStateFilterType,
} from "app/bfx-interface-controls/helpers/AlgoFilterHelper";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
import { COLUMN_DEALT_DIR_DATA } from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";

const FLASH_INTERVAL: number = 1000;
const HEDGE_ORDERS_PROFILE_KEY_SUFFIX = "bmarkHedgeOrders";
const BLOTTER_PREFERENCES_KEY = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;

/**
 * Main BmarkHedgeOrdersBlotterComponent used for displaying hedge orders in the BM Summary view that uses the global
 * instance of a BMarkHedgeOrderService and should never be marked as embedded
 */
@Component({
  selector: "bmark-hedge-orders-blotter",
  templateUrl: "./html/bmark-hedge-orders-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BmarkHedgeOrdersBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BmarkHedgeOrdersBlotterComponent
  extends BfxBaseBlotter
  implements AfterViewInit, OnDestroy, OnInit, OnChanges
{
  protected static inMemoryReady: Subject<void> = new ReplaySubject<any>();
  protected static initialized: boolean = false;
  protected static primed: boolean = false;
  protected static refreshSubject: Subject<any> = new Subject<any>();
  protected static queuedRowData: Array<any> = [];
  protected static rawData: any = {};
  protected static refreshColumns: any = {};
  protected static transaction: any = {
    add: [],
    remove: [],
    update: [],
    pending: {},
    lastUpdate: 0,
  };
  // TODO - consider dropping in BMs - no mechanism to switch complicates service
  public mode = OrdersTableMode.DEALT;

  protected get dealtMode(): boolean {
    // don't use in template
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  protected set dealtMode(isDealt: boolean) {
    if (isDealt === true && OrdersTableMode.BASE === this.mode) {
      this.mode = OrdersTableMode.DEALT;
    } else if (isDealt === true && OrdersTableMode.NDF_BASE === this.mode) {
      this.mode = OrdersTableMode.NDF_DEALT;
    } else if (OrdersTableMode.DEALT === this.mode) {
      this.mode = OrdersTableMode.BASE;
    } else if (OrdersTableMode.NDF_DEALT === this.mode) {
      this.mode = OrdersTableMode.NDF_BASE;
    }
    this.service.mode = this.mode;
  }

  private lastItemCounter: number = -1;
  public isFullTempStyle: boolean = true;

  public flashRow: boolean = false;
  protected flashTimer: any;

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    fillRenderer: FillRenderer,
    fillPercRenderer: FillPercentRenderer,
    autoHedgeRenderer: AutoHedgeRenderer,
  };

  @Input()
  public orderIDsFromInput: any;

  @Input()
  public doubleClickOpenOrderDetails = false;

  @Input()
  public embeddedMode: boolean = false;

  @Input()
  public parentComponent: any = null;

  /** Order state filter applied to all algos */
  @Input() public stateFilter: AlgoStateFilterType = AlgoStateFilterType.ALL;

  public blotterMenuId: string = null;

  private subscriptions: Subscription[] = [];
  private rendererSettings: { intentStylingEnabled: boolean };

  private static menuIdCounter: number = 0;

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BmarkHedgeOrdersDataService,
    protected _contextMenuServices: BmarkHedgeOrdersContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: BmarkHedgeOrdersTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    protected filteringService: BmarkHedgeOrdersFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected bmarkSummaryUtilService: BmarkSummaryUtilService,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuServices,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );

    this.blotterId =
      this.constructor.name +
      (this.embeddedMode ? "_embedded" + BmarkHedgeOrdersBlotterComponent : "");

    this.profileActionService.getProfileConfig(BLOTTER_PREFERENCES_KEY).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.subscriptions.push(
      this.profileActionService
        .getProfileConfigUpdates(BLOTTER_PREFERENCES_KEY)
        .subscribe((profileValue) => {
          this.mapUserSettings(profileValue);
        }),
    );
  }
  public getTableDefinitionContext(): any {
    return { embeddedMode: this.embeddedMode };
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.blotterId = this.constructor.name + (this.embeddedMode ? "_embedded" : "");
    this.blotterMenuId = this.embeddedMode
      ? this.blotterId + BmarkHedgeOrdersBlotterComponent.menuIdCounter++
      : this.blotterId;
  }

  ngAfterViewInit() {
    this.updateGridData();

    if (!this.embeddedMode) {
      const quickFilterSubscription = this.bmarkSummaryUtilService.quickFilterSubject.subscribe(
        this.onQuickFilterChanged.bind(this),
      );
      this.subscriptions.push(quickFilterSubscription);
    }

    this.flashTimer = setInterval(this.updateFlashRow.bind(this), FLASH_INTERVAL);
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.flashTimer != null) {
      clearInterval(this.flashTimer);
    }
  }

  /**
   * Detect incoming changes to the state filter and apply
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ("stateFilter" in changes) {
      const change: SimpleChange = changes["stateFilter"];
      if (!change.firstChange) {
        logger.out.info("bhob-c - refreshing Hedge Order state filter");
        if (this.gridOptions.api) {
          this.gridOptions.api.onFilterChanged();
          this.afterFilteringCompleted();
        }
      }
    }
  }

  /**
   * Extract the styling settings and add to the grid context
   * @param profileValue
   */
  private mapUserSettings(profileValue: any) {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        const intentStylingEnabled = savedSettings.intentStylingEnabled === "y";
        if (intentStylingEnabled != this.rendererSettings.intentStylingEnabled) {
          this.rendererSettings.intentStylingEnabled = intentStylingEnabled;
          this.refreshRendererSettings();
        }
      }
    } catch (e) {
      logger.out.warn(`bhob-c - settings error with value: 'profileValue' : ${e}`);
    }
  }

  /**
   * Refresh renderers that refernce changed context data
   */
  private refreshRendererSettings() {
    setTimeout(() => {
      logger.out.info("bhob-c - Refreshing intent col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_DEALT_DIR_DATA],
        force: true,
      });
    }, 100);
  }

  /**
   * Override to add in the contextual settings values for renderers
   * @returns
   */
  protected override initGridOptionsContext(): any {
    let retContext = super.initGridOptionsContext();
    // Add a blotterSettings - this is called from constructor so do init here rather than as property initialiser
    this.rendererSettings = { intentStylingEnabled: false };
    retContext.rendererSettings = this.rendererSettings;
    return retContext;
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions: GridOptions = super.createsGridOptions();

    gridOptions.rowClassRules = {
      pending: function (params) {
        return CubiqTS.OrderState.PENDING == params.data.orderState;
      },
      paused: this.checkPausedStyle.bind(this),
      hot: this.checkHotStyle.bind(this),
      external: this.checkExternalStyle.bind(this),
      // not all of these acceptance states are currently in use in client orders
      // later specification may diverge colors from orders blotter
      cancel_acceptance_needed: function (params) {
        return CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      modify_acceptance_needed: function (params) {
        return CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      expiry_acceptance_needed: function (params) {
        return CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      migration_acceptance_needed: function (params) {
        return CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      acceptance_needed: function (params) {
        return CubiqTS.OrderState.ACCEPTANCE_NEEDED == params.data.orderState;
      },
      // not applicable to BMs:
      // 'trigger_expiry_acceptance_needed': function (params) { return CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState },
      // 'suspended': function (params) { return CubiqTS.OrderState.SUSPENDED === params.data.orderState },
      // OCO only:
      // 'pending_cancellation': function (params) { return CubiqTS.OrderState.PENDING_CANCELLATION == params.data.orderState },
      rowFlash: function (params) {
        return params.data.rowFlash;
      },
    };

    gridOptions.cacheQuickFilter = false;

    return gridOptions;
  }

  protected initializeInMemory(): void {
    super.initializeInMemory();
  }

  public saveAsDefault() {
    super.saveAsDefault();
  }

  protected updateFlashRow(): void {
    this.flashRow = !this.flashRow;
  }

  protected getBadgeNumber(): number {
    return this.service.getItemCount();
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  /*     public getComponentConfiguration(): any {
        return super.getComponentConfiguration();
    } */

  public getGridOptions() {
    return this.gridOptions;
  }

  public getBlotterTypeStr(): string {
    return HEDGE_ORDERS_PROFILE_KEY_SUFFIX;
  }

  public setBmarkViewType(tableView: BmarkClientOrdersBlotterMode): void {
    this.service.setBlotterMode(tableView);
  }

  protected areUpdatesFrozen(): boolean {
    return false;
  }

  /****************
   * click events *
   ****************/

  public onCellDoubleClicked(event) {
    if (
      event == Object(event) &&
      event.node === Object(event.node) &&
      typeof event.node.id === "string"
    ) {
      // open Order Details from embedded blotter
      if (this.doubleClickOpenOrderDetails) {
        this._contextMenuServices.fireActionEvent(
          BfxBlotterAction.ACTION_VIEW_DETAILS,
          this,
          [event.node.id],
          [event.node.data],
          null,
          null,
          null,
          event,
        );
      } else {
        let id =
          event.node.data instanceof CubiqTS.OrderDataImpl &&
          event.node.data.blotterMode === BmarkClientOrdersBlotterMode.RISK
            ? event.node.data.orderId.toString()
            : event.node.id;
        this._contextMenuServices.fireActionEvent(
          BfxBlotterAction.ACTION_VIEW_DETAILS,
          this,
          [id],
          [event.node.data],
          null,
          null,
          null,
          event,
        );
      }
    }
  }

  /**
   *  maybe needed later
   *
   * public showSettings(event: Event): void {
   *  this.layoutService.showSettings('Benchmark', event);
   *   }
   */

  /** HELPER FUNCTIONS **/

  private checkHotStyle(params): boolean {
    let isHot = false;

    if (this.checkPausedStyle(params)) return false;

    switch (params.data.orderState) {
      case CubiqTS.OrderState.FILL_EXPIRED:
      case CubiqTS.OrderState.FILL_ERROR:
      case CubiqTS.OrderState.CANCELLED:
      case CubiqTS.OrderState.REJECTED:
      case CubiqTS.OrderState.EXPIRED:
        isHot = true;
        break;
      default:
        break;
    }
    return isHot;
  }
  /**
   * all acceptance states have a higher precidence than pause
   *
   * does not yet apply a css row but could be considered
   * @param params
   */
  protected checkAnyAcceptanceStyle(params): boolean {
    if (params.data) {
      let order = params.data;
      switch (order.orderState) {
        case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.PENDING_CANCELLATION:
        case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
          return true;
        default:
          return false;
      }
    }
    return false;
  }

  /**
   * pause has a higher precidence than trigger, temperature on row, and
   * primary / jira priority temperature cell styles.
   * @param params
   */
  protected checkPausedStyle(params): boolean {
    return OrderPauseState.isPausedOrderState(params.data);
  }

  protected checkExternalStyle(params): boolean {
    return (
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED == params.data.orderState
    );
  }

  public setEmbeddedTable(
    rows: any,
    config?: any,
    allowListOverrideConfig?: Map<string, object>,
  ): void {
    super.setEmbeddedTable(rows, config, allowListOverrideConfig, true);

    if (
      allowListOverrideConfig != null &&
      allowListOverrideConfig.has &&
      allowListOverrideConfig.has("sortingModel")
    ) {
      this.sortingModel = allowListOverrideConfig.get("sortingModel");
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(
          (() => {
            this.gridOptions.api.setSortModel(this.sortingModel);
          }).bind(this),
          100,
        );
      }
    }

    if (config instanceof BmarkFixingData && this.fullUpdateSubscription == null) {
      this.deleteSubscription = this.service.deleteItemSubject.subscribe(
        this.onDeleteEvent.bind(this),
      );
      this.fullUpdateSubscription = this.service.fullUpdateSubject.subscribe(
        this.onFullUpdateEvent.bind(this),
      );

      this.gridOptions.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);
      this.gridOptions.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
      this.defaultSortingModel = this._tableDefinitionFactory.getDefaultSortingModel();

      this.service.setEmbeddedBlotter(config);
      this.service.onFixingManagerEvent(config as BmarkFixingData);
      this.embeddedContextMenuService = this._contextMenuServices;

      if (this.blotterFilterBar != null) {
        this.blotterFilterBar.refresh(true, true);
        this.updateGridData();
      }
    }
  }

  protected onDeleteEvent(rowId: any): void {
    super.onDeleteEvent(rowId);
  }

  public isExternalFilterPresent(): boolean {
    return true;
  }

  public filterAppliedEvent(): void {
    this.service.onFilterApplied();
  }

  protected afterFilteringCompleted(): void {
    this.updateGridData({ redrawRows: true });
  }

  protected onQuickFilterChanged(searchText: string): void {
    if (this.gridOptions != null && this.gridOptions.api != null) {
      this.gridOptions.api.setQuickFilter(searchText);
      setTimeout(this.updateQuickFilterData.bind(this), 0);
    }
  }

  protected updateQuickFilterData(): void {
    this.gridDataUpdated();
    this.service.onFilterApplied();
    this.bmarkSummaryUtilService.isFilterApplied(this.service.filterAppliedObj);
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    let ret: boolean = true;
    let orderData: BmarkOrderData = node.data;

    if (
      this.embeddedTableMode &&
      !AlgoFilterHelper.doesStateFilterPass(this.stateFilter, orderData)
    ) {
      return false;
    }

    if (this.filteringService && this.blotterFilterBar) {
      let filters: Array<BfxBlotterFilter> = this.blotterFilterBar.getSelectedFilters();

      try {
        if (
          (filters != null && filters.length > 0) ||
          (this.embeddedTableMode == false && this.filteringService.hasViewFilter())
        ) {
          ret = this.filteringService.doFiltersPass(
            node,
            filters,
            false,
            this.dealtMode,
            this.embeddedTableMode,
          );
        }
      } catch (error) {
        logger.out.error("Error while doing a filter pass.", error);
      }
    }

    return ret;
  }

  public override restoreFromConfiguration(config: any): void {
    super.restoreFromConfiguration(config);
  }
}
